import React, { lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import theme from './themes';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import LoadingState from './components/LoadingState';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SiteProvider } from './contexts/SiteContext';

// Public pages
const HomePage = lazy(() => import('./pages/HomePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const OAuthCallback = lazy(() => import('./pages/OAuthCallback'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const CheckoutConfirmPage = lazy(() => import('./pages/CheckoutConfirmPage'));

// Admin pages
const AdminOverviewPage = lazy(() => import('./pages/AdminOverviewPage'));
const AllSitesPage = lazy(() => import('./pages/AllSitesPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const RBACPage = lazy(() => import('./pages/RBACPage'));
const AuditLogPage = lazy(() => import('./pages/AuditLogPage'));
const ImpersonationPage = lazy(() => import('./pages/ImpersonationPage'));
const DesignSessionsPage = lazy(() => import('./pages/DesignSessionsPage'));
const CampaignsPage = lazy(() => import('./pages/CampaignsPage'));
const WorkBoardPage = lazy(() => import('./pages/WorkBoardPage'));
const TriageConfigPage = lazy(() => import('./pages/TriageConfigPage'));
const OrchestratorPage = lazy(() => import('./pages/OrchestratorPage'));
const MySitesPage = lazy(() => import('./pages/MySitesPage'));
const SiteDetailPage = lazy(() => import('./pages/SiteDetailPage'));
const ManagedWebPresencePage = lazy(() => import('./pages/ManagedWebPresencePage'));

const PAGE_TITLES: Record<string, string> = {
  '/admin-overview': 'Overview',
  '/all-sites': 'All Sites',
  '/managed-web-presence': 'Managed Web Presence',
  '/analytics': 'Analytics',
  '/rbac': 'Roles & Permissions',
  '/audit-log': 'Audit Log',
  '/impersonation': 'Impersonation',
  '/design-sessions': 'Design Sessions',
  '/campaigns': 'Campaigns',
  '/work-board': 'Work Board',
  '/triage-config': 'Triage Guardrails',
  '/orchestrator': 'Process Orchestrator',
  '/my-sites': 'My Sites',
  '/settings': 'Settings',
};

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    const title = PAGE_TITLES[pathname] || 'UserInTheLoop';
    document.title = `${title} - UserInTheLoop`;
  }, [pathname]);
  return null;
};

const DRAWER_WIDTH = 260;

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    const currentPath = window.location.pathname + window.location.search;
    return <Navigate to={`/login?redirect=${encodeURIComponent(currentPath)}`} replace />;
  }

  return <>{children}</>;
}

function HomeRedirect() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  return isAuthenticated ? <Navigate to="/admin-overview" replace /> : <HomePage />;
}

function AppLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <ProtectedRoute>
      <SiteProvider>
        <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
          <Sidebar mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
            }}
          >
            <TopBar onMenuClick={() => setMobileOpen(true)} />
            <Box
              component="main"
              id="main-content"
              sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: 'background.default' }}
            >
              <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
                <Suspense fallback={<LoadingState />}>
                  <Routes>
                    <Route path="/admin-overview" element={<AdminOverviewPage />} />
                    <Route path="/all-sites" element={<AllSitesPage />} />
                    <Route path="/analytics" element={<AnalyticsPage />} />
                    <Route path="/rbac" element={<RBACPage />} />
                    <Route path="/audit-log" element={<AuditLogPage />} />
                    <Route path="/impersonation" element={<ImpersonationPage />} />
                    <Route path="/design-sessions" element={<DesignSessionsPage />} />
                    <Route path="/campaigns" element={<CampaignsPage />} />
                    <Route path="/work-board" element={<WorkBoardPage />} />
                    <Route path="/triage-config" element={<TriageConfigPage />} />
                    <Route path="/orchestrator" element={<OrchestratorPage />} />
                    <Route path="/my-sites" element={<MySitesPage />} />
                    <Route path="/my-sites/:siteId" element={<SiteDetailPage />} />
                    <Route path="/managed-web-presence" element={<ManagedWebPresencePage />} />
                    <Route path="/settings" element={<SettingsPage />} />

                    <Route path="/" element={<Navigate to="/admin-overview" replace />} />
                    <Route path="*" element={<Navigate to="/admin-overview" replace />} />
                  </Routes>
                </Suspense>
              </Box>
            </Box>
          </Box>
        </Box>
      </SiteProvider>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <ScrollToTop />
          <Suspense fallback={<LoadingState />}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/oauth/callback" element={<OAuthCallback />} />
              {/* PUBLIC — the firm lands here after Stripe Checkout; must not require auth */}
              <Route path="/managed-sites/:siteId/confirm" element={<CheckoutConfirmPage />} />
              <Route path="/" element={<HomeRedirect />} />
              <Route path="/*" element={<AppLayout />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
